import { useState, type FormEvent } from 'react';
import Sheet from './Sheet.js';
import { updateTask } from './api.js';
import { isoToLocalInput, localInputToIso, taskCode, TASK_STATUS_LABEL } from './format.js';
import { errorText, useToast } from './toast.js';
import type { TaskPatch, TaskRepeat, TaskRow, TaskStatus } from './types.js';

const STATUSES: TaskStatus[] = ['inbox', 'next', 'waiting', 'someday'];
const REPEATS: (TaskRepeat | '')[] = ['', 'daily', 'weekdays', 'weekly', 'biweekly', 'monthly', 'yearly'];

export interface Draft {
  title: string;
  notes: string;
  status: TaskStatus;
  dueOn: string;
  remindAt: string;
  waitingOn: string;
  startOn: string;
  priority: string;
  estimateMin: string;
  repeat: TaskRepeat | '';
}

export function toDraft(t: TaskRow): Draft {
  return {
    title: t.title,
    notes: t.notes ?? '',
    status: t.status,
    dueOn: t.dueOn ?? '',
    remindAt: isoToLocalInput(t.remindAt),
    waitingOn: t.waitingOn ?? '',
    startOn: t.startOn ?? '',
    priority: String(t.priority),
    estimateMin: t.estimateMin === null ? '' : String(t.estimateMin),
    repeat: t.repeat ?? '',
  };
}

/** Only the fields that changed, in the API's types. Exported for tests. */
export function taskPatch(before: Draft, after: Draft): TaskPatch {
  const p: TaskPatch = {};
  const blank = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  if (after.title.trim() !== before.title) p.title = after.title.trim();
  if (after.notes !== before.notes) p.notes = after.notes;
  if (after.status !== before.status) p.status = after.status;
  if (after.dueOn !== before.dueOn) p.dueOn = blank(after.dueOn);
  if (after.remindAt !== before.remindAt) p.remindAt = localInputToIso(after.remindAt);
  if (after.waitingOn !== before.waitingOn) p.waitingOn = blank(after.waitingOn);
  if (after.startOn !== before.startOn) p.startOn = blank(after.startOn);
  if (after.priority !== before.priority) p.priority = Number(after.priority) as 1 | 2 | 3;
  if (after.estimateMin !== before.estimateMin) {
    const n = Number(after.estimateMin);
    p.estimateMin = after.estimateMin.trim() === '' || !Number.isFinite(n) ? null : Math.round(n);
  }
  if (after.repeat !== before.repeat) p.repeat = after.repeat === '' ? null : after.repeat;
  return p;
}

/** Edit a task's details. Saving is an edit, not a state change: no undo window. */
export default function TaskEditSheet({
  task,
  onClose,
  onSaved,
}: {
  task: TaskRow;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const toast = useToast();
  const [initial] = useState(() => toDraft(task));
  const [d, setD] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = <K extends keyof Draft>(k: K, v: Draft[K]): void => setD((x) => ({ ...x, [k]: v }));

  async function save(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!d.title.trim()) {
      setErr('A task needs a title.');
      return;
    }
    const patch = taskPatch(initial, d);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      await updateTask(task.id, patch);
      toast.info(`Saved ${taskCode(task.shortCode)}`);
      onSaved();
      onClose();
    } catch (error) {
      setErr(errorText(error));
      setSaving(false);
    }
  }

  return (
    <Sheet title={`Edit ${taskCode(task.shortCode)}`} onClose={onClose}>
      <form className="settings-form lo-form" onSubmit={save}>
        <label className="settings-field settings-field--wide">
          Title
          <input className="settings-input" value={d.title} onChange={(e) => set('title', e.target.value)} />
        </label>
        <label className="settings-field">
          Status
          <select className="settings-input" value={d.status} onChange={(e) => set('status', e.target.value as TaskStatus)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {TASK_STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="settings-field">
          Priority
          <select className="settings-input" value={d.priority} onChange={(e) => set('priority', e.target.value)}>
            <option value="1">1 · high</option>
            <option value="2">2 · normal</option>
            <option value="3">3 · low</option>
          </select>
        </label>
        <label className="settings-field">
          Due
          <input className="settings-input" type="date" value={d.dueOn} onChange={(e) => set('dueOn', e.target.value)} />
        </label>
        <label className="settings-field">
          Remind me at
          <input
            className="settings-input"
            type="datetime-local"
            value={d.remindAt}
            onChange={(e) => set('remindAt', e.target.value)}
          />
        </label>
        {d.status === 'waiting' && (
          <>
            <label className="settings-field">
              Waiting on
              <input className="settings-input" value={d.waitingOn} onChange={(e) => set('waitingOn', e.target.value)} />
            </label>
            <label className="settings-field">
              Chase on
              <input className="settings-input" type="date" value={d.startOn} onChange={(e) => set('startOn', e.target.value)} />
            </label>
          </>
        )}
        <label className="settings-field">
          Minutes
          <input
            className="settings-input"
            type="number"
            min="1"
            inputMode="numeric"
            value={d.estimateMin}
            onChange={(e) => set('estimateMin', e.target.value)}
          />
        </label>
        <label className="settings-field">
          Repeats
          <select className="settings-input" value={d.repeat} onChange={(e) => set('repeat', e.target.value as TaskRepeat | '')}>
            {REPEATS.map((r) => (
              <option key={r} value={r}>
                {r === '' ? 'Never' : r}
              </option>
            ))}
          </select>
        </label>
        <label className="settings-field settings-field--wide">
          Notes
          <textarea className="settings-input lo-textarea" rows={3} value={d.notes} onChange={(e) => set('notes', e.target.value)} />
        </label>
        {err && (
          <p className="lo-form__error settings-field--wide" role="alert">
            {err}
          </p>
        )}
        <div className="lo-form__actions settings-field--wide">
          <button type="submit" className="btn" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Sheet>
  );
}
